import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, Tooltip, Alert, Space } from 'antd';
import { InfoCircleOutlined, DatabaseOutlined, BarChartOutlined, FileTextOutlined } from '@ant-design/icons';

const { Option } = Select;
const { TextArea } = Input;

const CustomDimensionModal = ({
  visible,
  onCancel,
  onSave,
  editingDimension,
  presetDimensions
}) => {
  const [form] = Form.useForm();
  const [baseOnValue, setBaseOnValue] = useState(['none']);

  useEffect(() => {
    if (visible) {
      if (editingDimension) {
        // 编辑模式，填充表单
        form.setFieldsValue({
          name: editingDimension.name,
          code: editingDimension.code,
          baseOn: Array.isArray(editingDimension.baseOn) ? editingDimension.baseOn : [editingDimension.baseOn],
          description: editingDimension.description
        });
        setBaseOnValue(Array.isArray(editingDimension.baseOn) ? editingDimension.baseOn : [editingDimension.baseOn]);
      } else {
        // 新增模式，重置表单
        form.resetFields();
        setBaseOnValue(['none']);
        form.setFieldsValue({
          baseOn: ['none']
        });
      }
    }
  }, [visible, editingDimension, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();

      // 确保baseOn是数组格式，并处理数据
      const dimensionData = {
        ...values,
        baseOn: Array.isArray(values.baseOn) ? values.baseOn : [values.baseOn]
      };

      onSave(dimensionData);
    } catch (error) {
      console.error('表单验证失败:', error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  const handleBaseOnChange = (value) => {
    // 如果选择了"无"，清空其他选择
    if (value.includes('none')) {
      setBaseOnValue(['none']);
      // 同步更新表单值
      form.setFieldsValue({ baseOn: ['none'] });
    } else {
      // 如果当前包含"无"，移除它
      const filteredValue = value.filter(v => v !== 'none');
      setBaseOnValue(filteredValue);
      // 同步更新表单值
      form.setFieldsValue({ baseOn: filteredValue });
    }
  };

  const getPresetDimensionIcon = (key) => {
    const iconMap = {
      'document': <FileTextOutlined />,
      'rule_avg': <BarChartOutlined />,
      'rule_detail': <DatabaseOutlined />
    };
    return iconMap[key] || <DatabaseOutlined />;
  };

  const getPresetDimensionDescription = (key) => {
    const descMap = {
      'document': '以整张业务单据为单位进行统计',
      'rule_avg': '以（单据×规则）实例为单位，取平均值',
      'rule_detail': '以单据内具体审核规则为单位'
    };
    return descMap[key] || '';
  };

  const validateCode = (_, value) => {
    if (!value) {
      return Promise.reject(new Error('请输入维度代码'));
    }
    if (value.length > 50) {
      return Promise.reject(new Error('维度代码不能超过50个字符'));
    }
    return Promise.resolve();
  };

  return (
    <Modal
      title={
        <Space>
          <DatabaseOutlined />
          {editingDimension ? '编辑自定义维度' : '添加自定义维度'}
        </Space>
      }
      open={visible}
      onOk={handleOk}
      onCancel={handleCancel}
      width={600}
      okText="确定"
      cancelText="取消"
      destroyOnClose
    >
      <Alert
        message="提示"
        description="自定义维度将基于所选预设维度进行二次分组统计，用于更精细化的数据分析。每个维度都包含5个预设指标和可添加的自定义指标。"
        type="info"
        showIcon
        className="mb-4"
      />

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          baseOn: ['none']
        }}
      >
        <Form.Item
          label="维度名称"
          name="name"
          rules={[
            { required: true, message: '请输入维度名称' },
            { max: 20, message: '维度名称不能超过20个字符' }
          ]}
        >
          <Input 
            placeholder="例如：文件类型维度" 
            maxLength={20}
            showCount
          />
        </Form.Item>

        <Form.Item
          label={
            <Space>
              维度代码
              <Tooltip title="用于在脚本中引用该维度，可以使用任意字符">
                <InfoCircleOutlined className="text-gray-400" />
              </Tooltip>
            </Space>
          }
          name="code"
          rules={[{ validator: validateCode }]}
        >
          <Input 
            placeholder="例如：CHANGE_ITEM、changeItem、change_item" 
            maxLength={50}
            showCount
          />
        </Form.Item>

        <Form.Item
          label={
            <Space>
              基于维度
              <Tooltip title="选择要基于哪个预设维度进行二次分组，或选择无表示独立维度">
                <InfoCircleOutlined className="text-gray-400" />
              </Tooltip>
            </Space>
          }
          name="baseOn"
          rules={[{ required: true, message: '请选择基于维度' }]}
        >
          <Select
            mode="multiple"
            onChange={handleBaseOnChange}
            placeholder="请选择基于维度"
          >
            <Option key="none" value="none">
              <Space>
                <span className="text-gray-500">-</span>
                无
              </Space>
            </Option>
            {presetDimensions.map(dim => (
              <Option key={dim.key} value={dim.key}>
                <Space>
                  <span className="text-blue-500">{getPresetDimensionIcon(dim.key)}</span>
                  {dim.name}
                </Space>
              </Option>
            ))}
          </Select>
        </Form.Item>

        {baseOnValue && baseOnValue.length > 0 && !baseOnValue.includes('none') && (
          <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
            {baseOnValue.map(value => {
              const dimension = presetDimensions.find(d => d.key === value);
              return dimension ? (
                <div key={value} className="mb-3 last:mb-0">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-blue-500">{getPresetDimensionIcon(value)}</span>
                    <span className="font-medium text-blue-700">{dimension.name}</span>
                  </div>
                  <p className="text-sm text-blue-600 mb-0">
                    {getPresetDimensionDescription(value)}
                  </p>
                </div>
              ) : null;
            })}
          </div>
        )}

        {baseOnValue && baseOnValue.includes('none') && (
          <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center space-x-2 mb-2">
              <span className="text-gray-500">-</span>
              <span className="font-medium text-gray-700">独立维度</span>
            </div>
            <p className="text-sm text-gray-600 mb-0">
              该维度不基于任何预设维度，作为独立的统计维度使用
            </p>
          </div>
        )}

        <Form.Item
          label="描述信息"
          name="description"
          rules={[
            { required: true, message: '请输入描述信息' },
            { max: 100, message: '描述信息不能超过100个字符' }
          ]}
        >
          <TextArea 
            placeholder="例如：按文件类型统计规则明细，用于分析不同文件类型的审核效果"
            rows={3}
            maxLength={100}
            showCount
          />
        </Form.Item>
      </Form>

      <Alert
        message="维度配置说明"
        description={
          <div className="space-y-2 text-sm">
            <div><strong>维度结构</strong>：每个维度包含5个预设指标（无风险精确率、无风险召回率、有风险精确率、有风险召回率、准确率）和可添加的自定义指标</div>
            <div><strong>基于维度选择</strong>：</div>
            <div className="ml-4">
              <div>• <strong>无</strong>：创建独立的统计维度，不依赖任何预设维度</div>
              <div>• <strong>单据维度</strong>：基于单据维度进行二次分组，在单据级别上按指定字段分类统计</div>
              <div>• <strong>规则平均维度</strong>：基于规则平均维度进行二次分组，在规则平均级别上按指定字段分类统计</div>
              <div>• <strong>规则明细维度</strong>：基于规则明细维度进行二次分组，在规则明细级别上按指定字段分类统计</div>
            </div>
          </div>
        }
        type="info"
        showIcon
        className="mt-4"
      />
    </Modal>
  );
};

export default CustomDimensionModal;
